import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService } from '../services/users.service';
import type { UserFilters, PaginationParams, CreateUserDto, UpdateUserDto } from '../types/user.types';

const USERS_KEY = 'users';
const USER_KEY = 'user';

/**
 * Hook pour récupérer la liste paginée des utilisateurs
 */
export function useUsers(
  filters: UserFilters = {},
  pagination: Partial<PaginationParams> = {}
) {
  return useQuery({
    queryKey: [USERS_KEY, filters, pagination],
    queryFn: () => usersService.getAll(filters, pagination),
  });
}

/**
 * Hook pour récupérer un utilisateur par son ID
 */
export function useUser(id: number | undefined) {
  return useQuery({
    queryKey: [USER_KEY, id],
    queryFn: () => usersService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook pour créer un utilisateur (Keycloak + base BPA)
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserDto) => usersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
    },
  });
}

/**
 * Hook pour mettre à jour un utilisateur
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserDto }) =>
      usersService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_KEY, id] });
    },
  });
}

/**
 * Hook pour supprimer un utilisateur
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
    },
  });
}
